// Angular
import { Injectable } from '@angular/core';

// RxJS
import { take, tap } from 'rxjs';

// Component Library
import { FsxOption, FsxOptionFactory } from 'fsx-component-library';

// Data Contracts
import { ListResponseOfState, State } from '@data-contracts';

// Local
import { UserTypeConstants, UserTypeObj } from '@app/model/user-types.model';
import { OrganizationsApiService } from '@app/services/api/organizations/organizations-api.services';

/**
 * A service that provides common FsxOption collections used throughout the application.
 */
@Injectable({ providedIn: 'root' })
export class CommonOptionsService {

  /**
   * The dropdown options for all "State" dropdowns.
   */
  stateOptions!: FsxOption<string>[];

  /**
   * The dropdown options for all "Suffix" dropdowns.
   */
  suffixOptions: FsxOption<string>[] = [
    { value: 'Jr.', label: 'Jr.' },
    { value: 'Sr.', label: 'Sr.' },
    { value: 'I', label: 'I' },
    { value: 'II', label: 'II' },
    { value: 'III', label: 'III' },
    { value: 'IV', label: 'IV' },
    { value: 'V', label: 'V' },
  ];

  /**
   * The dropdown options for all "User Type" dropdowns.
   */
  userTypeOptions!: FsxOption<UserTypeObj>[];

  /**
   * Constructor
   * 
   * @param fsxOptionFactory The factory service for creating FsxOption instances.
   * @param orgApiService The API service for retrieving organization data.
   */
  constructor(
    private readonly fsxOptionFactory: FsxOptionFactory,
    private readonly orgApiService: OrganizationsApiService
  ) { }

  /**
   * A public function to trigger the loading of API dependent options.
   */
  loadOptions(): void {
    this.loadStateOptions();
    this.loadUserTypeOptions();
  }

  /**
   * A private helper function to load the dropdown instances for all "State" dropdowns.   
   */
  private loadStateOptions(): void {
    this.orgApiService.getStates().pipe(
      take(1),
      tap((response: ListResponseOfState) => {
        // Delegate the creation of the options to the FsxOptionFactoryService.
        const userItems: State[] = response.data;
        const options = this.fsxOptionFactory.createOptions(userItems, 'name', 'code');
        this.stateOptions = options;
      })
    ).subscribe();
  }

  /**
   * A private helper function to load the dropdown instances for all "User Type" dropdowns.  
   */
  private loadUserTypeOptions(): void {
    this.userTypeOptions = UserTypeConstants.getAll().map((userType: UserTypeObj) => {
      return { value: userType, label: userType.caption };
    });
  }
}
